import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCopy,
  FiCreditCard,
  FiEdit2,
  FiGift,
  FiLink,
  FiPlus,
  FiRotateCcw,
  FiSearch,
  FiSlash,
  FiUser,
  FiX,
} from "react-icons/fi";
import { QRCodeSVG } from "qrcode.react";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

const EMPTY_CREATE_FORM = {
  customer_mode: "existing",
  customer_id: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  initial_amount: "",
  expires_at: "",
  note: "",
};

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function formatMoney(value, currency = "EUR") {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency }).format(Number(value || 0));
}

function formatDate(value, withTime = false) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
}

function statusLabel(account) {
  if (account?.unavailable_reason === "expired") return "Expired";
  return account?.status === "blocked" ? "Blocked" : account?.status === "closed" ? "Closed" : "Active";
}

function movementLabel(movement) {
  if (movement.movement_type === "reversal") return "Reversal";
  return Number(movement.amount) < 0 ? "Consumption" : "Credit";
}

function sourceLabel(value) {
  return {
    account_created: "Opening balance",
    admin_topup: "Manual credit",
    service_display: "Service display",
    admin_reversal: "Manual reversal",
    ticket_payment: "POS ticket",
  }[value] || value || "-";
}

export default function CustomerCredit() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [accounts, setAccounts] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modal, setModal] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [amountForm, setAmountForm] = useState({ amount: "", note: "" });
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [accessLink, setAccessLink] = useState("");
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState(null);
  const limit = 50;

  const loadAccounts = useCallback(async (nextOffset = 0) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/pos/customer-credit/accounts", {
        params: {
          business_unit_id: businessUnitId,
          q: debouncedSearch,
          status: statusFilter,
          limit,
          offset: nextOffset,
        },
      });
      setAccounts(res.data?.rows || []);
      setCount(Number(res.data?.count || 0));
      setOffset(nextOffset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load customer credit accounts.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, debouncedSearch, statusFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => { loadAccounts(0); }, [loadAccounts]);

  useEffect(() => {
    if (modal !== "create" || createForm.customer_mode !== "existing") return undefined;
    const timer = window.setTimeout(async () => {
      try {
        setCustomerLoading(true);
        const res = await axios.get("/api/pos/customer-credit/customer-options", {
          params: { business_unit_id: businessUnitId, q: customerSearch },
        });
        setCustomerOptions(res.data?.rows || []);
      } catch (err) {
        setError(err?.response?.data?.error || "Could not search customers.");
      } finally {
        setCustomerLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [businessUnitId, createForm.customer_mode, customerSearch, modal]);

  const totalVisibleBalance = useMemo(
    () => accounts.reduce((sum, account) => sum + Number(account.balance_amount || 0), 0),
    [accounts]
  );

  const closeModal = () => {
    setModal(null);
    setSelectedAccount(null);
    setCreateForm(EMPTY_CREATE_FORM);
    setCustomerSearch("");
    setCustomerOptions([]);
    setAmountForm({ amount: "", note: "" });
    setMovements([]);
    setReverseTarget(null);
    setReverseReason("");
    setAccessLink("");
  };

  const openCreate = () => {
    setError("");
    setMessage("");
    setCreateForm(EMPTY_CREATE_FORM);
    setModal("create");
  };

  const submitCreate = async () => {
    if (createForm.customer_mode === "existing" && !createForm.customer_id) {
      setError("Select a customer.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        business_unit_id: businessUnitId,
        customer_id: createForm.customer_mode === "existing" ? createForm.customer_id : null,
        new_customer: createForm.customer_mode === "new" ? {
          first_name: createForm.first_name,
          last_name: createForm.last_name,
          email: createForm.email,
          phone: createForm.phone,
        } : undefined,
        initial_amount: createForm.initial_amount || 0,
        expires_at: createForm.expires_at ? `${createForm.expires_at}T23:59:59.999` : null,
        note: createForm.note,
      };
      const res = await axios.post("/api/pos/customer-credit/accounts", payload);
      setSelectedAccount(res.data?.account || null);
      setAccessLink(res.data?.access_url || "");
      setModal("link");
      setMessage("Customer credit account created.");
      await loadAccounts(0);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not create the customer credit account.");
    } finally {
      setSaving(false);
    }
  };

  const openTopUp = (account) => {
    setSelectedAccount(account);
    setAmountForm({ amount: "", note: "" });
    setError("");
    setModal("topup");
  };

  const submitTopUp = async () => {
    try {
      setSaving(true);
      setError("");
      await axios.post(`/api/pos/customer-credit/accounts/${selectedAccount.id}/credit`, {
        business_unit_id: businessUnitId,
        amount: amountForm.amount,
        note: amountForm.note,
      });
      setMessage(`Credit added to ${selectedAccount.display_name}.`);
      closeModal();
      await loadAccounts(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not add credit.");
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (account) => {
    try {
      setSelectedAccount(account);
      setModal("history");
      setMovementsLoading(true);
      setError("");
      const res = await axios.get(`/api/pos/customer-credit/accounts/${account.id}/movements`, {
        params: { business_unit_id: businessUnitId },
      });
      setSelectedAccount(res.data?.account || account);
      setMovements(res.data?.rows || []);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load credit movements.");
    } finally {
      setMovementsLoading(false);
    }
  };

  const reverseMovement = async () => {
    if (!reverseReason.trim()) {
      setError("Enter the reason for the reversal.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await axios.post(`/api/pos/customer-credit/movements/${reverseTarget.id}/reverse`, {
        business_unit_id: businessUnitId,
        reason: reverseReason,
      });
      setReverseTarget(null);
      setReverseReason("");
      setMessage("Movement reversed. The original entry remains in the ledger.");
      await openHistory(selectedAccount);
      await loadAccounts(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not reverse this movement.");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (account) => {
    const nextStatus = account.status === "active" ? "blocked" : "active";
    try {
      setActionId(account.id);
      setError("");
      await axios.patch(`/api/pos/customer-credit/accounts/${account.id}`, {
        business_unit_id: businessUnitId,
        status: nextStatus,
      });
      setMessage(`${account.display_name} marked as ${nextStatus}.`);
      await loadAccounts(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update the account.");
    } finally {
      setActionId(null);
    }
  };

  const generateAccessLink = async (account) => {
    if (!window.confirm("Generate a new customer link? The previous link and its pending QR codes will stop working.")) return;
    try {
      setActionId(account.id);
      setError("");
      const res = await axios.post(`/api/pos/customer-credit/accounts/${account.id}/access-link`, {
        business_unit_id: businessUnitId,
      });
      setSelectedAccount(res.data?.account || account);
      setAccessLink(res.data?.access_url || "");
      setModal("link");
      await loadAccounts(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not generate the customer link.");
    } finally {
      setActionId(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(accessLink);
      setMessage("Customer link copied.");
    } catch (_err) {
      setError("Could not copy the link automatically.");
    }
  };

  return (
    <div className="reservations-container rm-page posCustomerCreditPage">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Customer credit</h3>
            <div className="rm-rates-subtitle">
              {loading ? "Loading accounts..." : `${count.toLocaleString("es-ES")} accounts · ${formatMoney(totalVisibleBalance)} visible balance`}
            </div>
          </div>
          <div className="rm-topActions">
            <div className="posCreditSearchField"><FiSearch /><input className="rm-searchInput" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search customer or email" /></div>
            <select className="posSelect" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
              <option value="closed">Closed</option>
            </select>
            <button type="button" className="rm-topBtn" onClick={() => { setSearch(""); setStatusFilter(""); }}><FiX /> Clear</button>
            <button type="button" className="rm-topBtn rm-topBtn--green" onClick={openCreate}><FiPlus /> Add credit account</button>
          </div>
        </div>
      </div>

      {error && !modal ? <div className="posStateBanner">{error}</div> : null}
      {message ? <div className="posInfoBanner">{message}</div> : null}

      <div className="rm-rates-card">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">Credit accounts</h3>
          <div className="rm-rates-subtitle">Independent balances with an auditable movement history.</div>
        </div>
        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">{loading ? "Loading..." : `Showing ${count ? offset + 1 : 0}-${Math.min(count, offset + accounts.length)} of ${count.toLocaleString("es-ES")}`}</small>
          <div className="rm-topActions">
            <button type="button" className="rm-topBtn" disabled={offset <= 0} onClick={() => loadAccounts(Math.max(0, offset - limit))}>Previous</button>
            <button type="button" className="rm-topBtn" disabled={offset + limit >= count} onClick={() => loadAccounts(offset + limit)}>Next</button>
          </div>
        </div>
        <div className="rm-rates-table-wrap">
          <table className="rm-rates-table" style={{ "--rm-col1-width": "260px", "--rm-table-min-width": "1360px" }}>
            <colgroup><col style={{ width: 260 }} /><col style={{ width: 130 }} /><col style={{ width: 160 }} /><col style={{ width: 160 }} /><col style={{ width: 160 }} /><col style={{ width: 150 }} /><col style={{ width: 170 }} /><col style={{ width: 370 }} /></colgroup>
            <thead><tr>
              <th className="res-th rm-sticky-1"><div className="res-thBtn"><span className="res-thLabel">Customer</span><span className="res-thSub">Account holder</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Status</span><span className="res-thSub">Availability</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Balance</span><span className="res-thSub">Available</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Credited</span><span className="res-thSub">Net total</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Consumed</span><span className="res-thSub">Net total</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Expiry</span><span className="res-thSub">Optional</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Last movement</span><span className="res-thSub">Activity</span></div></th>
              <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Actions</span><span className="res-thSub">Credit management</span></div></th>
            </tr></thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td className="rm-sticky-1"><strong className="posCreditCustomerName">{account.display_name}</strong><small className="posDeviceMeta">{account.email || `Customer #${account.customer_id}`}</small></td>
                  <td><span className={`rm-chip ${account.is_available ? "rm-chip--ok" : account.status === "blocked" ? "rm-chip--info" : "rm-chip--off"}`}>{statusLabel(account)}</span></td>
                  <td><strong className="posCreditBalance">{formatMoney(account.balance_amount, account.currency_code)}</strong></td>
                  <td>{formatMoney(account.total_credited_amount, account.currency_code)}</td>
                  <td>{formatMoney(account.total_redeemed_amount, account.currency_code)}</td>
                  <td>{formatDate(account.expires_at)}</td>
                  <td>{formatDate(account.last_movement_at, true)}</td>
                  <td><div className="posDeviceActions">
                    <button type="button" className="rm-topBtn rm-topBtn--green" onClick={() => openTopUp(account)} disabled={actionId === account.id || account.status === "closed"}><FiPlus /> Credit</button>
                    <button type="button" className="rm-topBtn" onClick={() => openHistory(account)} disabled={actionId === account.id}><FiEdit2 /> Movements</button>
                    <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={() => generateAccessLink(account)} disabled={actionId === account.id}><FiLink /> New link</button>
                    <button type="button" className={`rm-topBtn ${account.status === "active" ? "rm-topBtn--amber" : "rm-topBtn--green"}`} onClick={() => changeStatus(account)} disabled={actionId === account.id || account.status === "closed"}><FiSlash /> {account.status === "active" ? "Block" : "Activate"}</button>
                  </div></td>
                </tr>
              ))}
              {!accounts.length ? <tr><td colSpan="8" className="posMuted">No customer credit accounts found.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>

      {modal === "create" ? (
        <div className="posModalOverlay" role="presentation">
          <section className="posTicketModal posProductModal posProductModal--wide" role="dialog" aria-modal="true" aria-labelledby="credit-create-title">
            <div className="posTicketModalHeader"><div><h3 id="credit-create-title" className="rm-rates-title">Add credit account</h3><div className="rm-rates-subtitle">Create an independent customer balance and its private access link.</div></div><button type="button" className="res-filterCloseBtn" onClick={closeModal} aria-label="Close"><FiX /></button></div>
            {error ? <div className="posStateBanner">{error}</div> : null}
            <div className="posProductModalBody">
              <div className="posCreditModeTabs">
                <button type="button" className={createForm.customer_mode === "existing" ? "posCreditModeTab--active" : ""} onClick={() => setCreateForm((current) => ({ ...current, customer_mode: "existing", customer_id: "" }))}><FiSearch /> Existing customer</button>
                <button type="button" className={createForm.customer_mode === "new" ? "posCreditModeTab--active" : ""} onClick={() => setCreateForm((current) => ({ ...current, customer_mode: "new", customer_id: "" }))}><FiUser /> New customer</button>
              </div>
              {createForm.customer_mode === "existing" ? (
                <div className="posCreditCustomerPicker">
                  <label className="posModalField"><span>Search customer</span><input className="posTextInput" value={customerSearch} onChange={(event) => setCustomerSearch(event.target.value)} placeholder="Name, email or phone" /></label>
                  <div className="posCreditCustomerResults">
                    {customerOptions.map((customer) => <button key={customer.id} type="button" className={String(createForm.customer_id) === String(customer.id) ? "posCreditCustomerOption--selected" : ""} onClick={() => setCreateForm((current) => ({ ...current, customer_id: customer.id }))}><span><strong>{customer.display_name}</strong><small>{[customer.email, customer.phone].filter(Boolean).join(" · ") || `Customer #${customer.id}`}</small></span><FiCreditCard /></button>)}
                    {!customerOptions.length ? <div className="posMuted">{customerLoading ? "Searching customers..." : "No customers found."}</div> : null}
                  </div>
                </div>
              ) : (
                <div className="posModalFormGrid">
                  <label className="posModalField"><span>First name</span><input className="posTextInput" value={createForm.first_name} onChange={(event) => setCreateForm((current) => ({ ...current, first_name: event.target.value }))} /></label>
                  <label className="posModalField"><span>Last name</span><input className="posTextInput" value={createForm.last_name} onChange={(event) => setCreateForm((current) => ({ ...current, last_name: event.target.value }))} /></label>
                  <label className="posModalField"><span>Email</span><input type="email" className="posTextInput" value={createForm.email} onChange={(event) => setCreateForm((current) => ({ ...current, email: event.target.value }))} /></label>
                  <label className="posModalField"><span>Phone</span><input className="posTextInput" value={createForm.phone} onChange={(event) => setCreateForm((current) => ({ ...current, phone: event.target.value }))} /></label>
                </div>
              )}
              <div className="posModalFormGrid posCreditOpeningFields">
                <label className="posModalField"><span>Opening balance</span><input type="number" min="0" step="0.01" className="posTextInput" value={createForm.initial_amount} onChange={(event) => setCreateForm((current) => ({ ...current, initial_amount: event.target.value }))} placeholder="0,00" /></label>
                <label className="posModalField"><span>Expiry date · optional</span><input type="date" className="posTextInput" value={createForm.expires_at} onChange={(event) => setCreateForm((current) => ({ ...current, expires_at: event.target.value }))} /></label>
                <label className="posModalField posModalField--wide"><span>Opening note · optional</span><input className="posTextInput" value={createForm.note} onChange={(event) => setCreateForm((current) => ({ ...current, note: event.target.value }))} placeholder="Reason or internal reference" /></label>
              </div>
            </div>
            <div className="posProductModalFooter"><button type="button" className="rm-topBtn" onClick={closeModal} disabled={saving}>Cancel</button><button type="button" className="rm-topBtn rm-topBtn--green" onClick={submitCreate} disabled={saving}><FiGift /> {saving ? "Creating" : "Create account"}</button></div>
          </section>
        </div>
      ) : null}

      {modal === "topup" ? (
        <div className="posModalOverlay" role="presentation"><section className="posTicketModal posProductModal" role="dialog" aria-modal="true" aria-labelledby="credit-topup-title">
          <div className="posTicketModalHeader"><div><h3 id="credit-topup-title" className="rm-rates-title">Add credit</h3><div className="rm-rates-subtitle">{selectedAccount?.display_name} · Current balance {formatMoney(selectedAccount?.balance_amount)}</div></div><button type="button" className="res-filterCloseBtn" onClick={closeModal} aria-label="Close"><FiX /></button></div>
          {error ? <div className="posStateBanner">{error}</div> : null}
          <div className="posProductModalBody"><div className="posModalFormGrid"><label className="posModalField"><span>Amount</span><input autoFocus type="number" min="0.01" step="0.01" className="posTextInput" value={amountForm.amount} onChange={(event) => setAmountForm((current) => ({ ...current, amount: event.target.value }))} /></label><label className="posModalField posModalField--wide"><span>Note · optional</span><input className="posTextInput" value={amountForm.note} onChange={(event) => setAmountForm((current) => ({ ...current, note: event.target.value }))} /></label></div></div>
          <div className="posProductModalFooter"><button type="button" className="rm-topBtn" onClick={closeModal}>Cancel</button><button type="button" className="rm-topBtn rm-topBtn--green" onClick={submitTopUp} disabled={saving}><FiPlus /> {saving ? "Adding" : "Add credit"}</button></div>
        </section></div>
      ) : null}

      {modal === "link" ? (
        <div className="posModalOverlay" role="presentation"><section className="posTicketModal posProductModal" role="dialog" aria-modal="true" aria-labelledby="credit-link-title">
          <div className="posTicketModalHeader"><div><h3 id="credit-link-title" className="rm-rates-title">Customer access</h3><div className="rm-rates-subtitle">{selectedAccount?.display_name}</div></div><button type="button" className="res-filterCloseBtn" onClick={closeModal} aria-label="Close"><FiX /></button></div>
          {error ? <div className="posStateBanner">{error}</div> : null}
          <div className="posProductModalBody posCreditLinkBody"><div className="posCreditLinkQr"><QRCodeSVG value={accessLink} size={210} level="M" includeMargin /></div><p>This private page shows the live balance and a short-lived QR for the service display. Creating another link invalidates this one.</p><input className="posTextInput" value={accessLink} readOnly /></div>
          <div className="posProductModalFooter"><button type="button" className="rm-topBtn" onClick={closeModal}>Close</button><button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={copyLink}><FiCopy /> Copy link</button></div>
        </section></div>
      ) : null}

      {modal === "history" ? (
        <div className="posModalOverlay" role="presentation"><section className="posTicketModal posCreditHistoryModal" role="dialog" aria-modal="true" aria-labelledby="credit-history-title">
          <div className="posTicketModalHeader"><div><h3 id="credit-history-title" className="rm-rates-title">Credit movements</h3><div className="rm-rates-subtitle">{selectedAccount?.display_name} · Balance {formatMoney(selectedAccount?.balance_amount)}</div></div><button type="button" className="res-filterCloseBtn" onClick={closeModal} aria-label="Close"><FiX /></button></div>
          {error ? <div className="posStateBanner">{error}</div> : null}
          {reverseTarget ? <div className="posCreditReversePanel"><div><strong>Reverse {movementLabel(reverseTarget).toLowerCase()} of {formatMoney(Math.abs(reverseTarget.amount))}?</strong><span>The original movement remains visible and a compensating entry will be created.</span></div><input className="posTextInput" value={reverseReason} onChange={(event) => setReverseReason(event.target.value)} placeholder="Reason for reversal" /><button type="button" className="rm-topBtn" onClick={() => { setReverseTarget(null); setReverseReason(""); }}>Cancel</button><button type="button" className="rm-topBtn rm-topBtn--amber" onClick={reverseMovement} disabled={saving}><FiRotateCcw /> Confirm reversal</button></div> : null}
          <div className="posCreditHistoryBody"><table className="rm-rates-table"><thead><tr><th>When</th><th>Movement</th><th>Source</th><th>Amount</th><th>Balance after</th><th>Note</th><th>Action</th></tr></thead><tbody>{movements.map((movement) => <tr key={movement.id}><td>{formatDate(movement.created_at, true)}</td><td>{movementLabel(movement)}</td><td>{movement.device_name || sourceLabel(movement.source_type)}</td><td><strong className={Number(movement.amount) < 0 ? "posCreditAmount--negative" : "posCreditAmount--positive"}>{Number(movement.amount) > 0 ? "+" : ""}{formatMoney(movement.amount)}</strong></td><td>{formatMoney(movement.balance_after)}</td><td>{movement.note || "-"}</td><td>{movement.movement_type !== "reversal" && !movement.reversed ? <button type="button" className="rm-topBtn" onClick={() => { setReverseTarget(movement); setReverseReason(""); }}><FiRotateCcw /> Reverse</button> : <span className="posMuted">{movement.reversed ? "Reversed" : "-"}</span>}</td></tr>)}{!movements.length ? <tr><td colSpan="7" className="posMuted">{movementsLoading ? "Loading movements..." : "No movements yet."}</td></tr> : null}</tbody></table></div>
          <div className="posProductModalFooter"><button type="button" className="rm-topBtn" onClick={closeModal}>Close</button><button type="button" className="rm-topBtn rm-topBtn--green" onClick={() => openTopUp(selectedAccount)}><FiPlus /> Add credit</button></div>
        </section></div>
      ) : null}
    </div>
  );
}
